import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";

/**
 * Minimal history router with NEXT-PARITY semantics: the app's pages call
 * window.history.pushState/replaceState directly (Trends drills, Transactions
 * filters, the range picker) and expect usePathname/useSearchParams to sync —
 * exactly what Next's native-History-API interop provided. React Router
 * ignores raw history calls, so we patch pushState/replaceState to notify
 * subscribers instead; popstate covers Back/Forward. ~60 lines, no deps,
 * fully ours.
 */

type Loc = { pathname: string; search: string };
let cached: Loc = read();
function read(): Loc {
  return { pathname: window.location.pathname, search: window.location.search };
}
const listeners = new Set<() => void>();
function notify() {
  const next = read();
  if (next.pathname === cached.pathname && next.search === cached.search) return;
  cached = next;
  for (const l of [...listeners]) l();
}

const origPush = window.history.pushState.bind(window.history);
const origReplace = window.history.replaceState.bind(window.history);
window.history.pushState = (...args: Parameters<History["pushState"]>) => {
  origPush(...args);
  notify();
};
window.history.replaceState = (...args: Parameters<History["replaceState"]>) => {
  origReplace(...args);
  notify();
};
window.addEventListener("popstate", notify);

export function useAppLocation(): Loc {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cached,
  );
}

/** Client navigation: pushState + scroll reset (the Next router scrolled to
 * top on navigations; raw pushState doesn't). */
export function navigate(href: string, opts?: { replace?: boolean; scroll?: boolean }) {
  if (opts?.replace) window.history.replaceState(null, "", href);
  else {
    window.history.pushState(null, "", href);
    if (opts?.scroll !== false) window.scrollTo(0, 0);
  }
}

/** Route params for the useParams() shim — provided by App.tsx shells. */
export const ParamsContext = createContext<Record<string, string>>({});
export function RouteParams({ params, children }: { params: Record<string, string>; children: ReactNode }) {
  return <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>;
}
export function useParamsValue(): Record<string, string> {
  return useContext(ParamsContext);
}
